/**
 * Geoapify Map Service
 *
 * Implementation of the MapProvider interface using Geoapify tiles and MapLibre GL JS.
 * Provides map creation, styling, and overlay functionality for the Food Forest Planner.
 *
 * @module geoapify_map_service
 */

import maplibregl, { type StyleSpecification } from 'maplibre-gl';
import { MapStyleControl } from './map_style_control';
import { MAP_CONSTANTS } from '@/constants';
import type {
  MapProvider,
  MapOptions,
  MapTypeIds,
  Bounds,
  LatLng,
  Polygon,
  Marker,
  MarkerOptions,
  PolygonOptions,
  MapInstance,
} from '@/types/map_provider';

/**
 * Geoapify-specific implementation of the MapProvider interface.
 * Uses MapLibre GL JS for rendering and Geoapify for tile services.
 */
export class GeoapifyMapService implements MapProvider {
  private apiKey: string = '';
  private mapTilerApiKey: string = '';
  private loaded: boolean = false;

  /**
   * Load and initialize the Geoapify map service.
   * Retrieves API keys from meta tags and validates them.
   *
   * @throws Error if Geoapify API key is not found in meta tags
   */
  async load(): Promise<void> {
    if (this.loaded) {
      return;
    }

    this.apiKey = this.getMetaKey('geoapify-api-key');
    if (!this.apiKey) {
      throw new Error('Geoapify API key not found');
    }

    this.mapTilerApiKey = this.getMetaKey('maptiler-api-key');
    if (!this.mapTilerApiKey) {
      console.warn('MapTiler API key not found — satellite view will be unavailable');
    }

    this.loaded = true;
  }

  /**
   * Retrieve an API key from an HTML meta tag.
   *
   * @param name - The meta tag name attribute
   * @returns API key string or empty string if not found
   * @private
   */
  private getMetaKey(name: string): string {
    const meta = document.querySelector(`meta[name="${name}"]`);
    return meta ? meta.getAttribute('content') || '' : '';
  }

  /**
   * Create a MapLibre map instance with Geoapify tiles.
   *
   * @param element - HTML element to render the map into
   * @param options - Map configuration options
   * @returns Promise resolving to the MapLibre map instance
   */
  async createMap(element: HTMLElement, options: MapOptions): Promise<MapInstance> {
    await this.load();

    const style = this.getStyleUrl(options.mapTypeId);

    const map = new maplibregl.Map({
      container: element,
      style: style,
      center: [options.center.lng, options.center.lat],
      zoom: options.zoom,
    });

    // Log tile errors, including rate-limit (429) responses
    map.on('error', (e: { error?: { status?: number; message?: string }; message?: string }) => {
      const status = e.error?.status;
      if (status === 429) {
        console.error('[Map] Tile rate limit exceeded (HTTP 429). Check your API quota.', e);
      } else {
        console.error('[Map] Tile error:', e.error?.message ?? e.message ?? e);
      }
    });

    // Add map style control if requested (positioned at top-left)
    if (options.mapTypeControl) {
      const styleControl = new MapStyleControl(this.apiKey, this.mapTilerApiKey, options.mapTypeId, options.onStyleChanged ?? null);
      map.addControl(styleControl, 'top-left');
    }

    // Add navigation controls if requested (zoom controls at top-left below style control)
    if (options.zoomControl !== false) {
      map.addControl(new maplibregl.NavigationControl(), 'top-left');
    }

    return map;
  }

  /**
   * Get the style URL for a given map type.
   * Terrain uses Geoapify; Satellite uses MapTiler raster tiles.
   *
   * @param mapTypeId - Map type identifier (TERRAIN or SATELLITE)
   * @returns Complete style URL or style object with API key
   * @private
   */
  private getStyleUrl(mapTypeId: string): string | StyleSpecification {
    if (mapTypeId === 'SATELLITE') {
      return {
        version: 8,
        sources: {
          satellite: {
            type: 'raster',
            tiles: [`https://api.maptiler.com/tiles/satellite-v2/{z}/{x}/{y}.jpg?key=${this.mapTilerApiKey}`],
            tileSize: 256,
            attribution: '© <a href="https://www.maptiler.com/">MapTiler</a> © <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            maxzoom: 20,
          },
        },
        layers: [{ id: 'satellite', type: 'raster', source: 'satellite' }],
      };
    }

    // TERRAIN (default)
    return `https://maps.geoapify.com/v1/styles/klokantech-basic/style.json?apiKey=${this.apiKey}`;
  }

  /**
   * Create a bounds object for defining geographic boundaries.
   * Uses MapLibre's LngLatBounds.
   *
   * @returns Bounds object that can be extended with coordinate points
   */
  createBounds(): Bounds {
    return new maplibregl.LngLatBounds();
  }

  /**
   * Create a LatLng coordinate object.
   * Uses MapLibre's LngLat (note: MapLibre uses lng, lat order).
   *
   * @param lat - Latitude (-90 to 90)
   * @param lng - Longitude (-180 to 180)
   * @returns LatLng coordinate object
   */
  createLatLng(lat: number, lng: number): LatLng {
    const lngLat = new maplibregl.LngLat(lng, lat);
    return {
      lat: lngLat.lat,
      lng: lngLat.lng,
    };
  }

  /**
   * Create a polygon overlay on the map using GeoJSON layers.
   * MapLibre uses GeoJSON sources and layers instead of overlay objects.
   *
   * Note: This implementation adds the polygon directly to the map.
   * Returns a reference object for potential future manipulation.
   *
   * @param options - Polygon configuration options
   * @returns Promise resolving to a polygon reference object
   */
  async createPolygon(options: PolygonOptions): Promise<Polygon> {
    if (!options.map) {
      throw new Error('Map instance is required to create polygon');
    }

    const map = options.map as maplibregl.Map;
    const sourceId = `polygon-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Convert paths to GeoJSON coordinates (lng, lat order)
    const coordinates = options.paths.map((point) => [point.lng, point.lat]);

    // Wait for the map to be fully settled before adding sources/layers.
    // isStyleLoaded() is the correct synchronous check; if false, wait for
    // 'idle' which fires after style + tiles are ready in MapLibre v5.
    await new Promise<void>((resolve) => {
      if (map.isStyleLoaded()) {
        resolve();
      } else {
        map.once('idle', () => resolve());
      }
    });

    // Add GeoJSON source for the polygon
    map.addSource(sourceId, {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'Polygon',
          coordinates: [coordinates],
        },
      },
    });

    // Add fill layer
    map.addLayer({
      id: `${sourceId}-fill`,
      type: 'fill',
      source: sourceId,
      paint: {
        'fill-color': options.fillColor || MAP_CONSTANTS.DEFAULT_FILL_COLOR,
        'fill-opacity': options.fillOpacity !== undefined ? options.fillOpacity : 0.2,
      },
    });

    // Add line layer for the stroke
    map.addLayer({
      id: `${sourceId}-line`,
      type: 'line',
      source: sourceId,
      paint: {
        'line-color': options.strokeColor || MAP_CONSTANTS.DEFAULT_STROKE_COLOR,
        'line-width': options.strokeWeight || 3,
        'line-opacity': options.strokeOpacity !== undefined ? options.strokeOpacity : 1.0,
      },
    });

    // Return a reference object with cleanup method
    return {
      sourceId,
      setMap: (newMap: maplibregl.Map | null) => {
        if (!newMap) {
          // Remove layers and source if map is set to null
          if (map.getLayer(`${sourceId}-fill`)) {
            map.removeLayer(`${sourceId}-fill`);
          }
          if (map.getLayer(`${sourceId}-line`)) {
            map.removeLayer(`${sourceId}-line`);
          }
          if (map.getSource(sourceId)) {
            map.removeSource(sourceId);
          }
        }
      },
    };
  }

  /**
   * Create a marker at a specific location on the map.
   * Uses MapLibre's Marker with custom HTML content.
   *
   * @param options - Marker configuration options
   * @returns Promise resolving to the MapLibre marker instance
   */
  async createMarker(options: MarkerOptions): Promise<Marker> {
    const markerOptions: maplibregl.MarkerOptions = {};
    if (options.content) {
      markerOptions.element = options.content;
    }

    const marker = new maplibregl.Marker(markerOptions)
      .setLngLat([options.position.lng, options.position.lat]);

    // Add to map if provided
    if (options.map) {
      marker.addTo(options.map as maplibregl.Map);
    }

    return marker;
  }

  /**
   * Get map type ID constants.
   *
   * @returns Object with TERRAIN and SATELLITE identifiers
   */
  getMapTypeId(): MapTypeIds {
    return {
      TERRAIN: 'TERRAIN',
      SATELLITE: 'SATELLITE',
    };
  }
}
